import React from 'react';
import {
    Modal,
    Space,
    Tag,
    Row,
    Col,
    Typography,
    Timeline
} from 'antd';
import {
    TruckOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    CarOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const ShippingTimelineModal = ({ 
    visible, 
    onCancel, 
    trackingInfo 
}) => {
    // Get event icon based on status
    const getEventIcon = (status) => {
        const statusLower = status?.toLowerCase() || '';
        
        if (statusLower.includes('delivered')) {
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        }
        if (statusLower.includes('out for delivery')) {
            return <CarOutlined style={{ color: '#1890ff' }} />;
        }
        if (statusLower.includes('transit') || statusLower.includes('departed')) {
            return <TruckOutlined style={{ color: '#1890ff' }} />;
        }
        if (statusLower.includes('arrived')) {
            return <EnvironmentOutlined style={{ color: '#722ed1' }} />;
        }
        if (statusLower.includes('exception') || statusLower.includes('failed')) {
            return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
        }
        if (statusLower.includes('international')) {
            return <GlobalOutlined style={{ color: '#13c2c2' }} />;
        }
        return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
    };

    // Get event color
    const getEventColor = (status) => {
        const statusLower = status?.toLowerCase() || '';
        
        if (statusLower.includes('delivered')) return '#52c41a';
        if (statusLower.includes('out for delivery')) return '#1890ff';
        if (statusLower.includes('exception') || statusLower.includes('failed')) return '#ff4d4f';
        if (statusLower.includes('international')) return '#13c2c2';
        if (statusLower.includes('transit') || statusLower.includes('arrived')) return '#722ed1';
        return '#d9d9d9';
    };

    // Format date
    const formatEventDate = (dateString) => {
        try {
            const date = dayjs(dateString);
            const now = dayjs();
            const diffDays = now.diff(date, 'day');
            
            if (diffDays === 0) {
                return `Today, ${date.format('HH:mm')}`;
            } else if (diffDays === 1) {
                return `Yesterday, ${date.format('HH:mm')}`;
            } else if (diffDays < 7) {
                return `${diffDays} days ago, ${date.format('HH:mm')}`;
            } else {
                return date.format('MMM DD, YYYY HH:mm');
            }
        } catch (error) {
            return dateString;
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <TruckOutlined />
                    <span>Shipping Timeline</span>
                    {trackingInfo?.scrapeData?.status && (
                        <Tag color="#1890ff">
                            {trackingInfo.scrapeData.status}
                        </Tag>
                    )}
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={720}
            centered
        >
            {trackingInfo && (
                <>
                    {/* Tracking Info Header */}
                    <Row gutter={[16, 8]} style={{ 
                        marginBottom: 20, 
                        padding: '16px', 
                        backgroundColor: '#fafafa', 
                        borderRadius: 8,
                        border: '1px solid #e8e8e8'
                    }}>
                        <Col xs={24} sm={12}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Primary Tracking</Text>
                            <Text strong style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                                {trackingInfo.trackingNumber || 'N/A'}
                            </Text>
                            <div>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    via {trackingInfo.carrier || 'Unknown Carrier'}
                                </Text>
                            </div>
                        </Col>
                        
                        {trackingInfo.lastMileCarrier && trackingInfo.lastMileTracking && (
                            <Col xs={24} sm={12}>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Last Mile</Text>
                                <Text strong style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                                    {trackingInfo.lastMileTracking}
                                </Text>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                        via {trackingInfo.lastMileCarrier}
                                    </Text>
                                </div>
                            </Col>
                        )}
                    </Row>

                    {/* Timeline */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden', padding: '3px' }}>
                        <Timeline mode="left">
                            {trackingInfo.scrapeData?.events?.map((event, index) => (
                                <Timeline.Item
                                    key={event.eventId || index}
                                    dot={getEventIcon(event.status)}
                                    color={getEventColor(event.status)}
                                >
                                    <div style={{ marginLeft: 8 }}>
                                        <div style={{ marginBottom: 6 }}>
                                            <Text strong style={{ fontSize: '14px' }}>
                                                {event.status}
                                            </Text>
                                        </div>
                                        
                                        <Row gutter={[16, 4]}>
                                            <Col xs={24} sm={12}>
                                                <Space size={4} align="center">
                                                    <ClockCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        {formatEventDate(event.occurrenceDatetime)}
                                                    </Text>
                                                </Space>
                                            </Col>
                                            
                                            {event.location && (
                                                <Col xs={24} sm={12}>
                                                    <Space size={4} align="center">
                                                        <EnvironmentOutlined style={{ fontSize: '12px', color: '#999' }} />
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {event.location}
                                                        </Text>
                                                    </Space>
                                                </Col>
                                            )}
                                        </Row>

                                        {event.courierCode && (
                                            <div style={{ marginTop: 4 }}>
                                                <Tag size="small" color="blue">
                                                    {event.courierCode}
                                                </Tag>
                                            </div>
                                        )}
                                    </div>
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    </div>

                    {trackingInfo.lastScrapedAt && (
                        <div style={{ 
                            marginTop: 16, 
                            padding: '8px 12px', 
                            backgroundColor: '#f0f0f0', 
                            borderRadius: 4,
                            textAlign: 'center'
                        }}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                Last updated: {dayjs(trackingInfo.lastScrapedAt).format('MMM DD, YYYY HH:mm')}
                            </Text>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default ShippingTimelineModal;
